import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const domParser = new DOMParser();

/**
 * @typedef {Object} NcnProductQARefs
 * @property {HTMLElement} [tabList] - Tab list container (community mode only)
 * @property {HTMLElement[]} [tabs] - Tab buttons (community mode only)
 * @property {HTMLElement} panelFaq - FAQ tab panel
 * @property {HTMLElement} [panelCommunity] - Community tab panel (community mode only)
 * @property {HTMLElement} [faqList] - FAQ accordion list
 * @property {HTMLElement} [communityList] - Community accordion list (community mode only)
 * @property {HTMLElement} [communityLoading] - Loading skeleton
 * @property {HTMLElement} [communityEmpty] - Empty state
 * @property {HTMLElement} [communityError] - Error state
 * @property {HTMLElement} [communityCount] - Community tab count badge
 * @property {HTMLInputElement} [searchInput] - Search input
 * @property {HTMLTemplateElement} [communityItemTemplate] - Template for community items
 * @property {HTMLElement} [faqNoResults] - FAQ no search results
 * @property {HTMLElement} [communityNoResults] - Community no search results
 * @property {HTMLElement} [loadMoreBtn] - Load more button
 */

/**
 * Product Questions & Answers web component.
 * Manages tab switching (WAI-ARIA tab pattern), Okendo API fetch for community Q&A,
 * and debounced search filtering.
 *
 * @extends {Component<NcnProductQARefs>}
 */
class NcnProductQA extends Component {
  /** @type {boolean} */
  #communityEnabled = false;

  get requiredRefs() {
    const refs = ['panelFaq'];
    if (this.#communityEnabled) {
      refs.push('tabList', 'panelCommunity', 'communityList');
    }
    return refs;
  }

  /** @type {string} */
  #activeTab = 'faq';

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {Array<Object>} Accumulated community questions for JSON-LD schema */
  #allCommunityQuestions = [];

  /** @type {boolean} */
  #communityLoaded = false;

  /** @type {boolean} */
  #isFetching = false;

  /** @type {number} */
  #communityOffset = 0;

  /** @type {number} */
  #communityLimit = 25;

  /** @type {boolean} */
  #hasMoreCommunity = false;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedSearch = null;

  /** @type {HTMLScriptElement | null} */
  #communitySchemaScript = null;

  /** @type {AbortController | null} */
  #voteController = null;

  /** @type {Set<string>} */
  #cacheKeys = new Set();

  connectedCallback() {
    this.#communityEnabled = this.hasAttribute('data-community-enabled');
    super.connectedCallback();
    this.#debouncedSearch = debounce(() => {
      const query = this.refs.searchInput?.value?.toLowerCase().trim() ?? '';
      this.#filterItems('faq', query);
      if (this.#communityEnabled) {
        this.#filterItems('community', query);
      }
    }, 300);
    if (this.#communityEnabled) {
      this.#fetchCommunityQuestions();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fetchController?.abort();
    this.#fetchController = null;
    this.#voteController?.abort();
    this.#voteController = null;
    this.#debouncedSearch?.cancel();
    this.#communitySchemaScript?.remove();
    this.#communitySchemaScript = null;
  }

  /**
   * Re-initializes community state when the Section Rendering API
   * re-renders content in the theme editor.
   */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#communityEnabled) return;
    this.#communityLoaded = false;
    this.#communityOffset = 0;
    this.#allCommunityQuestions = [];
    this.refs.panelCommunity?.removeAttribute('aria-busy');
    this.#fetchCommunityQuestions();
  }

  /**
   * Switches to a tab by ID. Called via `on:click="/switchTab/faq"`.
   *
   * @param {string} tabId - "faq" or "community"
   */
  switchTab(tabId) {
    if (!this.#communityEnabled) return;
    if (this.#activeTab === tabId) return;
    this.#activeTab = tabId;

    const { tabs, panelFaq, panelCommunity } = this.refs;
    if (!tabs?.length) return;

    for (const tab of tabs) {
      const id = tab.getAttribute('aria-controls')?.includes('faq') ? 'faq' : 'community';
      const isActive = id === tabId;

      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('ncn-qa__tab--active', isActive);
    }

    if (tabId === 'faq') {
      panelFaq.hidden = false;
      panelCommunity.hidden = true;
    } else {
      panelFaq.hidden = true;
      panelCommunity.hidden = false;
      if (!this.#communityLoaded) {
        this.#fetchCommunityQuestions();
      }
    }
  }

  /**
   * Handles keyboard navigation within the tab list.
   * Arrow Left/Right to navigate, Home/End for first/last, Space/Enter to activate.
   *
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    if (!this.#communityEnabled) return;
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    let newIndex = -1;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        this.#activateTabByElement(/** @type {HTMLElement} */ (event.target));
        return;
      default:
        return;
    }

    if (newIndex >= 0) {
      tabs[newIndex].focus();
      this.#activateTabByElement(tabs[newIndex]);
    }
  }

  /**
   * Debounced search handler. Filters accordion items in both panels.
   */
  handleSearch() {
    this.#debouncedSearch?.();
  }

  /**
   * Handles vote button clicks. Toggles aria-pressed, updates counts optimistically,
   * and proxies the click to Okendo's native widget button.
   *
   * @param {string} voteType - "helpful" or "unhelpful"
   * @param {Event} event
   */
  handleVote(voteType, event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.target.closest('.ncn-qa__vote-btn');
    const details = button?.closest('[data-question-id]');
    if (!button || !details) return;
    if (button.classList.contains('ncn-qa__vote-btn--pending')) return;

    const voteGroup = button.closest('[data-vote-target]');
    const voteTarget = voteGroup?.getAttribute('data-vote-target') ?? 'question';
    const questionId = details.getAttribute('data-question-id');
    const entityId = voteTarget === 'answer'
      ? voteGroup?.getAttribute('data-answer-id')
      : questionId;
    const isPressed = button.getAttribute('aria-pressed') === 'true';

    // Find opposite button within the same vote group
    const buttons = [...(voteGroup?.querySelectorAll('.ncn-qa__vote-btn') ?? [])];
    const oppositeBtn = buttons.find((btn) => btn !== button);

    // Capture pre-vote state for rollback
    const snapshot = {
      button,
      oppositeBtn,
      buttonPressed: isPressed,
      oppositePressed: oppositeBtn?.getAttribute('aria-pressed') === 'true',
      buttonCount: button.querySelector('[data-slot$="Count"]')?.textContent ?? '0',
      oppositeCount: oppositeBtn?.querySelector('[data-slot$="Count"]')?.textContent ?? '0',
    };

    // Optimistic UI update
    if (isPressed) {
      button.setAttribute('aria-pressed', 'false');
      this.#updateVoteCount(button, -1);
    } else {
      button.setAttribute('aria-pressed', 'true');
      this.#updateVoteCount(button, 1);
      if (oppositeBtn?.getAttribute('aria-pressed') === 'true') {
        oppositeBtn.setAttribute('aria-pressed', 'false');
        this.#updateVoteCount(oppositeBtn, -1);
      }
    }

    button.classList.add('ncn-qa__vote-btn--pending');
    button.setAttribute('aria-disabled', 'true');

    this.#submitVote(questionId, entityId, voteType, isPressed, snapshot, voteTarget);
    this.#invalidateCache();
  }

  /**
   * Loads the next page of community questions.
   */
  loadMore() {
    if (!this.#communityEnabled) return;
    this.#fetchCommunityQuestions(true);
  }

  /**
   * Retries loading community questions after an error.
   */
  retry() {
    if (!this.#communityEnabled) return;
    this.#communityLoaded = false;
    this.#communityOffset = 0;
    this.#allCommunityQuestions = [];

    const { communityError, communityList } = this.refs;
    if (communityError) communityError.hidden = true;
    if (communityList) communityList.replaceChildren();

    this.#fetchCommunityQuestions();
  }

  /**
   * Updates the vote count text inside a button by a delta.
   *
   * @param {Element} button
   * @param {number} delta
   */
  #updateVoteCount(button, delta) {
    const countEl = button.querySelector('[data-slot$="Count"]');
    if (!countEl) return;
    const current = parseInt(countEl.textContent, 10) || 0;
    countEl.textContent = String(Math.max(0, current + delta));
  }

  /**
   * Submits a vote to the Okendo API for a question or answer.
   * Optimistic UI is already applied by the caller.
   *
   * @param {string} questionId - Parent question ID
   * @param {string} entityId - Question or answer ID (same as questionId for question votes)
   * @param {string} voteType - "helpful" or "unhelpful"
   * @param {boolean} wasPressed - Whether the button was already pressed (true = cancelling)
   * @param {Object} snapshot - Pre-vote UI state for rollback on failure
   * @param {string} voteTarget - "question" or "answer"
   */
  async #submitVote(questionId, entityId, voteType, wasPressed, snapshot, voteTarget) {
    const subscriberId = this.dataset.okendoSubscriberId;
    if (!subscriberId || !entityId) return;

    const direction = voteType === 'helpful' ? 'upvote' : 'downvote';
    const okeVoteType = wasPressed ? `${direction}-cancel` : direction;
    const basePath = `https://api.okendo.io/v1/stores/${encodeURIComponent(subscriberId)}/questions/${encodeURIComponent(questionId)}`;
    const url = voteTarget === 'answer'
      ? `${basePath}/answers/${encodeURIComponent(entityId)}/vote`
      : `${basePath}/vote`;

    this.#voteController = new AbortController();

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voteType: okeVoteType }),
        signal: this.#voteController.signal,
      });

      if (!res.ok) {
        this.#revertVote(snapshot);
      }
    } catch {
      this.#revertVote(snapshot);
    } finally {
      snapshot.button.classList.remove('ncn-qa__vote-btn--pending');
      snapshot.button.removeAttribute('aria-disabled');
    }
  }

  /**
   * Reverts optimistic vote UI changes using a captured snapshot.
   *
   * @param {Object} snapshot - Pre-vote UI state
   */
  #revertVote(snapshot) {
    const { button, oppositeBtn, buttonPressed, oppositePressed, buttonCount, oppositeCount } = snapshot;

    button.setAttribute('aria-pressed', String(buttonPressed));
    const countEl = button.querySelector('[data-slot$="Count"]');
    if (countEl) countEl.textContent = buttonCount;

    if (oppositeBtn) {
      oppositeBtn.setAttribute('aria-pressed', String(oppositePressed));
      const oppCountEl = oppositeBtn.querySelector('[data-slot$="Count"]');
      if (oppCountEl) oppCountEl.textContent = oppositeCount;
    }
  }

  /**
   * Activates a tab given the tab button element.
   *
   * @param {HTMLElement} tabElement
   */
  #activateTabByElement(tabElement) {
    const id = tabElement.getAttribute('aria-controls')?.includes('faq') ? 'faq' : 'community';
    this.switchTab(id);
  }

  /**
   * Fetches community questions from the Okendo API.
   *
   * @param {boolean} [loadMore=false] - Whether to append (true) or replace (false)
   */
  async #fetchCommunityQuestions(loadMore = false) {
    if (loadMore && this.#isFetching) return;
    this.#isFetching = true;
    this.refs.panelCommunity.setAttribute('aria-busy', 'true');

    const subscriberId = this.dataset.okendoSubscriberId;
    const productId = this.dataset.productId;
    const { communityLoading, communityEmpty, communityError, loadMoreBtn } = this.refs;

    if (!subscriberId || !productId) {
      if (communityLoading) communityLoading.hidden = true;
      if (communityError) communityError.hidden = false;
      this.#communityLoaded = true;
      this.#isFetching = false;
      this.refs.panelCommunity.removeAttribute('aria-busy');
      return;
    }

    if (!loadMore) {
      this.#communityOffset = 0;
      if (communityLoading) communityLoading.hidden = false;
    }

    const cacheKey = `okendo-qa-${subscriberId}-${productId}-${this.#communityOffset}`;
    const cached = this.#readCache(cacheKey);

    if (cached) {
      if (communityLoading) communityLoading.hidden = true;
      this.#processCommunityData(cached, loadMore);
      this.#isFetching = false;
      this.refs.panelCommunity.removeAttribute('aria-busy');
      return;
    }

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = `https://api.okendo.io/v1/stores/${encodeURIComponent(subscriberId)}/products/shopify-${encodeURIComponent(productId)}/questions?limit=${this.#communityLimit}&offset=${this.#communityOffset}`;

    try {
      const response = await fetch(url, {
        signal: this.#fetchController.signal,
      });

      if (response.status === 404) {
        if (communityLoading) communityLoading.hidden = true;
        if (communityEmpty) communityEmpty.hidden = false;
        this.#communityLoaded = true;
        this.#isFetching = false;
        this.refs.panelCommunity.removeAttribute('aria-busy');
        return;
      }
      if (!response.ok) throw new Error(`Okendo API returned ${response.status}`);

      const data = await response.json();
      this.#writeCache(cacheKey, data);

      if (communityLoading) communityLoading.hidden = true;
      this.#processCommunityData(data, loadMore);
      this.#isFetching = false;
      this.refs.panelCommunity.removeAttribute('aria-busy');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('Community Q&A fetch error:', error);
      if (communityLoading) communityLoading.hidden = true;
      if (communityError) communityError.hidden = false;
      this.#communityLoaded = true;
      this.#isFetching = false;
      this.refs.panelCommunity.removeAttribute('aria-busy');
    }
  }

  /**
   * Processes community Q&A data and renders items.
   *
   * @param {Object} data - Okendo API response
   * @param {boolean} loadMore - Whether appending to existing items
   */
  #processCommunityData(data, loadMore) {
    const { communityEmpty, loadMoreBtn } = this.refs;
    const questions = data.questions ?? [];

    if (!loadMore && questions.length === 0) {
      if (communityEmpty) communityEmpty.hidden = false;
      this.#communityLoaded = true;
      return;
    }

    if (communityEmpty) communityEmpty.hidden = true;

    const existingCount = this.refs.communityList?.querySelectorAll('.ncn-qa__accordion').length ?? 0;

    for (const q of questions) {
      this.#renderCommunityItem(q);
    }

    if (loadMore && questions.length > 0) {
      const items = this.refs.communityList?.querySelectorAll('.ncn-qa__accordion');
      /** @type {HTMLElement | undefined} */ (items?.[existingCount])?.focus();
    }

    this.#allCommunityQuestions.push(...questions);
    this.#communityOffset += questions.length;
    this.#hasMoreCommunity = questions.length >= this.#communityLimit;

    if (loadMoreBtn) {
      loadMoreBtn.hidden = !this.#hasMoreCommunity;
    }

    // Reapply active search filter to newly loaded items
    const query = this.refs.searchInput?.value?.toLowerCase().trim() ?? '';
    if (query) {
      this.#filterItems('community', query);
    }

    this.#updateCommunityCount();
    this.#injectCommunitySchema();
    this.#communityLoaded = true;
  }

  /** @type {number} Cache TTL in milliseconds (12 hours) */
  static #CACHE_TTL = 12 * 60 * 60 * 1000;

  /**
   * Reads a cached entry from localStorage.
   *
   * @param {string} key
   * @returns {Object | null}
   */
  #readCache(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (Date.now() - entry.ts > NcnProductQA.#CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  }

  /**
   * Writes data to localStorage with a timestamp.
   *
   * @param {string} key
   * @param {Object} data
   */
  #writeCache(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
      this.#cacheKeys.add(key);
    } catch {
      /* storage full — silently skip */
    }
  }

  /**
   * Removes all cached Q&A entries for this product so the next
   * page load fetches fresh data with updated vote counts.
   */
  #invalidateCache() {
    const subscriberId = this.dataset.okendoSubscriberId;
    const productId = this.dataset.productId;
    if (!subscriberId || !productId) return;

    for (const key of this.#cacheKeys) {
      localStorage.removeItem(key);
    }
    this.#cacheKeys.clear();
  }

  /**
   * Renders a single community question by cloning the hidden <template>.
   *
   * @param {Object} question - Okendo question object
   */
  #renderCommunityItem(question) {
    const template = this.refs.communityItemTemplate;
    if (!template) return;

    const clone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const details = clone.querySelector('details');
    if (!details) return;

    details.setAttribute('data-question-id', question.questionId);

    const questionSlot = clone.querySelector('[data-slot="question"]');
    const questionAuthorSlot = clone.querySelector('[data-slot="questionAuthor"]');
    const questionAvatarSlot = clone.querySelector('[data-slot="questionAvatar"]');
    const answerSlot = clone.querySelector('[data-slot="answer"]');
    const avatarSlot = clone.querySelector('[data-slot="avatar"]');
    const verifiedSlot = clone.querySelector('[data-slot="verified"]');
    const responderSlot = clone.querySelector('[data-slot="responder"]');
    const dateSlot = clone.querySelector('[data-slot="date"]');
    const questionHelpfulCountSlot = clone.querySelector('[data-slot="questionHelpfulCount"]');
    const questionUnhelpfulCountSlot = clone.querySelector('[data-slot="questionUnhelpfulCount"]');
    const helpfulCountSlot = clone.querySelector('[data-slot="helpfulCount"]');
    const unhelpfulCountSlot = clone.querySelector('[data-slot="unhelpfulCount"]');
    const answerRow = clone.querySelector('.ncn-qa__answer-row');

    // Okendo API sanitizes content server-side; safe to render as HTML
    if (questionSlot) questionSlot.innerHTML = question.body ?? '';
    const questionAuthorName = question.author?.displayName ?? '';
    if (questionAuthorSlot && questionAuthorName) {
      questionAuthorSlot.textContent = questionAuthorName;
    }
    if (questionAvatarSlot) {
      questionAvatarSlot.textContent = this.#getInitials(questionAuthorName);
    }

    const answer = question.answers?.[0];
    if (answer) {
      const answerVoteGroup = clone.querySelector('[data-vote-target="answer"]');
      if (answerVoteGroup && answer.answerId) {
        answerVoteGroup.setAttribute('data-answer-id', answer.answerId);
      }
      const name = answer.author?.displayName ?? '';
      // Okendo API sanitizes content server-side; safe to render as HTML
      if (answerSlot) answerSlot.innerHTML = answer.body ?? '';
      if (responderSlot) responderSlot.textContent = name;
      if (verifiedSlot && answer.isStoreAnswer) verifiedSlot.hidden = false;
      if (avatarSlot) {
        avatarSlot.textContent = this.#getInitials(name);
        if (answer.isStoreAnswer) avatarSlot.classList.add('ncn-qa__avatar--verified');
      }
      if (helpfulCountSlot) helpfulCountSlot.textContent = String(answer.helpfulCount ?? 0);
      if (unhelpfulCountSlot) unhelpfulCountSlot.textContent = String(answer.unhelpfulCount ?? 0);
    } else {
      if (answerSlot) {
        answerSlot.textContent = this.dataset.noAnswerText || 'No answer yet.';
        answerSlot.classList.add('ncn-qa__answer-text--empty');
      }
      if (answerRow) answerRow.classList.add('ncn-qa__answer-row--empty');
    }

    if (questionHelpfulCountSlot) questionHelpfulCountSlot.textContent = String(question.helpfulCount ?? 0);
    if (questionUnhelpfulCountSlot) questionUnhelpfulCountSlot.textContent = String(question.unhelpfulCount ?? 0);

    if (dateSlot && question.dateCreated) {
      const date = new Date(question.dateCreated);
      dateSlot.textContent = date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    this.refs.communityList?.appendChild(clone);
  }

  /**
   * Extracts up to two initials from a name string.
   *
   * @param {string} name
   * @returns {string}
   */
  #getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /** Updates the community tab count badge. */
  #updateCommunityCount() {
    const count = this.refs.communityList?.querySelectorAll('.ncn-qa__accordion').length ?? 0;
    const badge = this.refs.communityCount;
    if (badge) {
      badge.textContent = `(${count})`;
      badge.hidden = count === 0;
    }
  }

  /**
   * Injects (or updates) a FAQPage JSON-LD script for community Q&A items
   * that have at least one answer. Built from raw API data so it works
   * independently of DOM rendering and IntersectionObserver timing.
   */
  #injectCommunitySchema() {
    const entries = [];

    for (const q of this.#allCommunityQuestions) {
      const answer = q.answers?.[0];
      if (!q.body || !answer?.body) continue;

      entries.push({
        question: this.#stripHtml(q.body),
        answer: this.#stripHtml(answer.body),
      });
    }

    if (entries.length === 0) {
      this.#communitySchemaScript?.remove();
      this.#communitySchemaScript = null;
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: entries.map((e) => ({
        '@type': 'Question',
        name: e.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: e.answer,
        },
      })),
    };

    if (!this.#communitySchemaScript) {
      this.#communitySchemaScript = document.createElement('script');
      this.#communitySchemaScript.type = 'application/ld+json';
      document.head.appendChild(this.#communitySchemaScript);
    }

    this.#communitySchemaScript.textContent = JSON.stringify(schema);
  }

  /**
   * Strips HTML tags from a string and returns trimmed plain text.
   *
   * @param {string} html
   * @returns {string}
   */
  #stripHtml(html) {
    if (!html) return '';
    return domParser.parseFromString(html, 'text/html').body.textContent?.trim() ?? '';
  }

  /**
   * Filters accordion items in a panel by search query.
   *
   * @param {string} panelType - "faq" or "community"
   * @param {string} query - Lowercase search string
   */
  #filterItems(panelType, query) {
    const list = panelType === 'faq' ? this.refs.faqList : this.refs.communityList;
    const noResults = panelType === 'faq' ? this.refs.faqNoResults : this.refs.communityNoResults;
    if (!list) return;

    const items = list.querySelectorAll('.ncn-qa__accordion');
    let visibleCount = 0;

    for (const item of items) {
      const text = item.textContent?.toLowerCase() ?? '';
      const matches = !query || text.includes(query);
      item.setAttribute('data-search-hidden', String(!matches));
      if (matches) visibleCount++;
    }

    if (noResults) {
      noResults.hidden = visibleCount > 0 || items.length === 0;
    }
  }
}

if (!customElements.get('ncn-product-qa')) {
  customElements.define('ncn-product-qa', NcnProductQA);
}
